import { CloseIcon, DragHandleIcon } from '@chakra-ui/icons';
import React from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';

import {
  CmBox,
  CmButton,
  CmCard,
  CmFlex,
  CmIconButton,
  CmLabelAndSelect,
  CmModal,
  CmModalBody,
  CmModalFooter,
  CmStack,
  CmText,
} from '@/components/uiParts';
import {
  MenuManagementDeliveryStore,
  UpdateMenuSet,
} from '@/pages/menu-managements/allocated-menus/service';
import { SearchBrandMenuResponse } from '@/services/BrandMenu/hooks';
import { SearchBrandMenuHourResponse } from '@/services/BrandMenuHour/hooks/search/useMenuManagementBrandMenuHourSearch';

type Props = {
  isOpen: boolean;
  target: '更新' | '一括更新';
  brandMenuOptions: SearchBrandMenuResponse[];
  menuSets: UpdateMenuSet[];
  targetDeliveryStore?: MenuManagementDeliveryStore;
  brandMenuHourOptions: SearchBrandMenuHourResponse[];
  handleSubmit: () => void;
  handleCloseModal: () => void;
  handleChangeBrandMenu: (index: number, brandMenuId: string) => void;
  handleChangeBrandMenuHour: (index: number, brandMenuHourId: string) => void;
  handleAddMenuSet: () => void;
  handleRemoveMenuSet: (menuSetIndex: number) => void;
  handleOnDragEnd: (result: DropResult) => void;
};
export const MenuManagementAllocatedMenusModalInner: React.FC<Props> = ({
  isOpen,
  target,
  brandMenuOptions,
  menuSets,
  targetDeliveryStore,
  brandMenuHourOptions,
  handleSubmit,
  handleCloseModal,
  handleChangeBrandMenu,
  handleChangeBrandMenuHour,
  handleAddMenuSet,
  handleRemoveMenuSet,
  handleOnDragEnd,
}) => {
  const map = {
    更新: {
      isDisabled: (() => {
        if (targetDeliveryStore === undefined) return true;
        return menuSets.some(
          (menuSet) =>
            menuSet.brand_menu_id === '' || menuSet.brand_menu_hour_id === ''
        );
      })(),
    },
    一括更新: {
      isDisabled: (() => {
        return menuSets.some(
          (menuSet) =>
            menuSet.brand_menu_id === '' || menuSet.brand_menu_hour_id === ''
        );
      })(),
    },
  };

  const { isDisabled } = map[target];

  return (
    <CmBox as="form" onSubmit={handleSubmit}>
      <CmModal
        onClose={handleCloseModal}
        title={`店舗メニュー割り当て${target}`}
        {...{ isOpen }}
      >
        <CmModalBody pb="10">
          <CmStack spacing="15px">
            {menuSets.length > 0 ? (
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="LinkedGroups">
                  {(provided) => (
                    <ul {...provided.droppableProps} ref={provided.innerRef}>
                      {menuSets.map((menuSet, menuSetIndex) => {
                        const { brand_menu_id, brand_menu_hour_id } = menuSet;
                        const uniquebannerId = `group-${menuSetIndex}`;
                        return (
                          <Draggable
                            key={menuSetIndex}
                            draggableId={uniquebannerId}
                            index={menuSetIndex}
                          >
                            {(provided) => (
                              <li
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <CmCard my="10px">
                                  <CmFlex
                                    alignItems="center"
                                    position="relative"
                                  >
                                    <DragHandleIcon aria-label="ドラッグ" />
                                    <CmBox width="100%">
                                      <CmFlex
                                        alignItems="center"
                                        flexDirection="row-reverse"
                                        position="absolute"
                                        right="0"
                                        top="-9px"
                                      >
                                        <CmIconButton
                                          onClick={() =>
                                            handleRemoveMenuSet(menuSetIndex)
                                          }
                                          aria-label="削除"
                                          backgroundColor="transparent"
                                          icon={<CloseIcon />}
                                        />
                                      </CmFlex>

                                      <CmBox pl="20px">
                                        <CmStack spacing="10px">
                                          <CmLabelAndSelect
                                            width="90%"
                                            isRequired
                                            fontWeight="bold"
                                            id="menu"
                                            label={`メニュー`}
                                            value={brand_menu_id}
                                            onChange={(e) => {
                                              handleChangeBrandMenu(
                                                menuSetIndex,
                                                e.target.value
                                              );
                                            }}
                                          >
                                            <option hidden />
                                            {brandMenuOptions.map(
                                              (brandMenuOption) => {
                                                const { id, title } =
                                                  brandMenuOption;
                                                const titleJa =
                                                  title.translation.ja;
                                                return (
                                                  <option
                                                    disabled={
                                                      menuSet.brand_menu_id ===
                                                      id
                                                    }
                                                    key={id}
                                                    value={id}
                                                  >
                                                    {titleJa}
                                                  </option>
                                                );
                                              }
                                            )}
                                          </CmLabelAndSelect>
                                          <CmLabelAndSelect
                                            width="90%"
                                            isRequired
                                            fontWeight="bold"
                                            id="menu"
                                            label={`メニュー提供時間`}
                                            value={brand_menu_hour_id}
                                            onChange={(e) => {
                                              handleChangeBrandMenuHour(
                                                menuSetIndex,
                                                e.target.value
                                              );
                                            }}
                                          >
                                            <option hidden />
                                            {brandMenuHourOptions.map(
                                              (brandMenuHour) => {
                                                const { id, title } =
                                                  brandMenuHour;
                                                const titleJa =
                                                  title.translation.ja;
                                                return (
                                                  <option
                                                    disabled={
                                                      menuSet.brand_menu_hour_id ===
                                                      id
                                                    }
                                                    key={id}
                                                    value={id}
                                                  >
                                                    {titleJa}
                                                  </option>
                                                );
                                              }
                                            )}
                                          </CmLabelAndSelect>
                                        </CmStack>
                                      </CmBox>
                                    </CmBox>
                                  </CmFlex>
                                </CmCard>
                              </li>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </ul>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              <CmText color="red" textAlign="center" my="5">
                メニューを割り当てている場合は全て解除されます。
              </CmText>
            )}
            <CmFlex flexDirection="row-reverse">
              <CmButton onClick={handleAddMenuSet}>
                メニューと提供時間のペアを追加
              </CmButton>
            </CmFlex>
          </CmStack>
        </CmModalBody>
        <CmModalFooter>
          <CmFlex alignItems="center">
            <CmButton onClick={handleSubmit} {...{ isDisabled }}>
              {target}
            </CmButton>
          </CmFlex>
          {/*
           // @ts-ignore TS2590 */}
          <CmButton ml="5" variant="outline" onClick={handleCloseModal}>
            閉じる
          </CmButton>
        </CmModalFooter>
      </CmModal>
    </CmBox>
  );
};
